/**
 * London Smoke & Cure — Njord RTDB live hub (shallow paths only).
 *
 * NEVER subscribe to `live` or `live/{id}` roots — backlog under `live/{id}/backlog/*`
 * would re-download the entire subtree on every sample during soak / catch-up.
 * UI only needs latest, presence, health, reported, updateResult per sensor.
 *
 * Transport modes:
 * - poll (default): periodic Cloud Run facility live cache (or RTDB shallow get)
 * - realtime: onValue per shallow path for every registered sensor (avoid for wall / Overview)
 *
 * Sensor detail can focus one id for realtime even while the hub stays on poll.
 * Visibility: when the document is hidden, disconnect RTDB listeners / pause polls.
 */

import { get, onValue, ref } from 'firebase/database';
import { getNjordDatabase } from './njordFirebase';
import { fetchNjordSensors } from './njordRegistryService';
import { fetchNjordFacilityLive } from './njordFacilityLiveService';

/** @type {readonly string[]} */
export const NJORD_LIVE_SHALLOW_KEYS = ['latest', 'presence', 'health', 'reported', 'updateResult'];

/** Default poll cadence (ms) — Overview / wall / idle Njord shell. */
export const NJORD_LIVE_POLL_INTERVAL_MS = 60_000;

/** @type {Map<string, object>} */
const rowsBySensor = new Map();
/** @type {Map<string, (() => void)[]>} */
const unsubsBySensor = new Map();
/** @type {Set<(data: Record<string, object>) => void>} */
const listeners = new Set();
/** Sensor ids that need realtime shallow listeners (sensor detail) even in poll mode. */
const realtimeFocusIds = new Set();

let sensorIds = [];
let registrySyncTimer = null;
let registrySyncInFlight = false;
let notifyRaf = null;

/** @type {'realtime' | 'poll'} */
let transportMode = 'poll';
let pollIntervalMs = NJORD_LIVE_POLL_INTERVAL_MS;
/** @type {'rtdb' | 'api'} */
let pollSource = 'api';
let pollTimer = null;
let pollInFlight = false;
let visibilityPaused = false;
let visibilityBound = false;

function flushNotify() {
    notifyRaf = null;
    const snap = buildSnapshot();
    listeners.forEach((cb) => {
        try {
            cb(snap);
        } catch {
            /* subscriber error */
        }
    });
}

function scheduleNotify() {
    if (notifyRaf != null) return;
    if (typeof requestAnimationFrame === 'function') {
        notifyRaf = requestAnimationFrame(flushNotify);
        return;
    }
    notifyRaf = setTimeout(flushNotify, 0);
}
function buildSnapshot() {
    /** @type {Record<string, object>} */
    const out = {};
    rowsBySensor.forEach((row, id) => {
        if (row && Object.keys(row).length > 0) out[id] = row;
    });
    return out;
}

function notify() {
    scheduleNotify();
}

function mergeShallowKey(sensorId, key, value) {
    let row = rowsBySensor.get(sensorId);
    if (!row) {
        row = {};
        rowsBySensor.set(sensorId, row);
    }
    if (value == null) {
        delete row[key];
    } else {
        row[key] = value;
    }
    if (Object.keys(row).length === 0) {
        rowsBySensor.delete(sensorId);
    }
}

function replaceSensorRow(sensorId, row) {
    if (!row || typeof row !== 'object') {
        rowsBySensor.delete(sensorId);
        return;
    }
    const next = {};
    for (const key of NJORD_LIVE_SHALLOW_KEYS) {
        if (row[key] != null) next[key] = row[key];
    }
    if (Object.keys(next).length) rowsBySensor.set(sensorId, next);
    else rowsBySensor.delete(sensorId);
}

function teardownSensorListeners(sensorId) {
    const unsubs = unsubsBySensor.get(sensorId);
    if (unsubs) {
        unsubs.forEach((u) => u());
        unsubsBySensor.delete(sensorId);
    }
}

function teardownAllRealtimeListeners() {
    for (const id of [...unsubsBySensor.keys()]) {
        teardownSensorListeners(id);
    }
}

function clearPollTimer() {
    if (pollTimer != null) {
        clearInterval(pollTimer);
        pollTimer = null;
    }
}

function wantsRealtimeListeners(sensorId) {
    if (visibilityPaused) return false;
    if (transportMode === 'realtime') return true;
    return realtimeFocusIds.has(sensorId);
}

async function pollFromRtdb() {
    const db = getNjordDatabase();
    if (!db) return;
    await Promise.all(
        sensorIds.map(async (sensorId) => {
            await Promise.all(
                NJORD_LIVE_SHALLOW_KEYS.map(async (key) => {
                    try {
                        const snap = await get(ref(db, `live/${sensorId}/${key}`));
                        mergeShallowKey(sensorId, key, snap.exists() ? snap.val() : null);
                    } catch {
                        mergeShallowKey(sensorId, key, null);
                    }
                })
            );
        })
    );
}

async function pollFromApi() {
    const data = await fetchNjordFacilityLive();
    const live = data.live || {};
    const nextIds = Object.keys(live);
    if (nextIds.length) {
        const merged = [...new Set([...sensorIds, ...nextIds])];
        sensorIds = merged.sort();
    }
    for (const id of sensorIds) {
        replaceSensorRow(id, live[id] || null);
    }
}

async function pollOnce() {
    if (pollInFlight || visibilityPaused || transportMode !== 'poll') return;
    if (!listeners.size && !sensorIds.length) return;
    pollInFlight = true;
    try {
        if (pollSource === 'api') {
            try {
                await pollFromApi();
            } catch {
                await pollFromRtdb();
            }
        } else {
            await pollFromRtdb();
        }
        notify();
    } catch {
        /* keep last snapshot */
    } finally {
        pollInFlight = false;
    }
}

function ensureSensorListeners(sensorId) {
    if (!sensorId || unsubsBySensor.has(sensorId)) return;
    if (!wantsRealtimeListeners(sensorId)) return;
    const db = getNjordDatabase();
    if (!db) return;

    const unsubs = NJORD_LIVE_SHALLOW_KEYS.map((key) => {
        const pathRef = ref(db, `live/${sensorId}/${key}`);
        return onValue(
            pathRef,
            (snap) => {
                mergeShallowKey(sensorId, key, snap.exists() ? snap.val() : null);
                notify();
            },
            () => {
                mergeShallowKey(sensorId, key, null);
                notify();
            }
        );
    });
    unsubsBySensor.set(sensorId, unsubs);
}

function rebuildTransport() {
    teardownAllRealtimeListeners();
    clearPollTimer();
    if (visibilityPaused || !listeners.size) {
        notify();
        return;
    }
    if (transportMode === 'poll') {
        pollOnce();
        pollTimer = setInterval(pollOnce, Math.max(15_000, pollIntervalMs));
        /* Detail pages may still need one-sensor realtime while Overview polls. */
        for (const id of realtimeFocusIds) {
            ensureSensorListeners(id);
        }
        return;
    }
    sensorIds.forEach(ensureSensorListeners);
    notify();
}

function bindVisibility() {
    if (visibilityBound || typeof document === 'undefined') return;
    visibilityBound = true;
    const onVis = () => {
        visibilityPaused = Boolean(document.hidden);
        rebuildTransport();
    };
    document.addEventListener('visibilitychange', onVis);
    visibilityPaused = Boolean(document.hidden);
}

function applySensorIds(nextIds) {
    const normalized = [...new Set((nextIds || []).map((id) => String(id).trim()).filter(Boolean))].sort();
    const prevKey = sensorIds.join('\0');
    const nextKey = normalized.join('\0');
    if (prevKey === nextKey) return;
    sensorIds = normalized;

    const nextSet = new Set(normalized);
    for (const id of [...unsubsBySensor.keys()]) {
        if (!nextSet.has(id) && !realtimeFocusIds.has(id)) {
            teardownSensorListeners(id);
            rowsBySensor.delete(id);
        }
    }
    if (transportMode === 'realtime' && !visibilityPaused) {
        normalized.forEach(ensureSensorListeners);
    } else if (!visibilityPaused) {
        for (const id of realtimeFocusIds) {
            ensureSensorListeners(id);
        }
    }
    notify();
}

async function refreshSensorIdsFromRegistry() {
    if (registrySyncInFlight) return;
    registrySyncInFlight = true;
    try {
        const registry = await fetchNjordSensors();
        const ids = (registry || [])
            .filter((r) => r && !r.archived)
            .map((r) => r.id || r.sensorId)
            .filter(Boolean);
        if (ids.length) applySensorIds(ids);
    } catch {
        /* registry unavailable — keep existing listeners */
    } finally {
        registrySyncInFlight = false;
    }
}

function ensureRegistrySync() {
    if (registrySyncTimer != null) return;
    refreshSensorIdsFromRegistry();
    if (typeof window !== 'undefined') {
        registrySyncTimer = window.setInterval(refreshSensorIdsFromRegistry, 5 * 60 * 1000);
    }
}

/**
 * @param {{
 *   mode?: 'realtime' | 'poll',
 *   pollIntervalMs?: number,
 *   pollSource?: 'rtdb' | 'api',
 * }} opts
 */
export function setNjordLiveTransport(opts = {}) {
    if (opts.mode === 'realtime' || opts.mode === 'poll') {
        transportMode = opts.mode;
    }
    if (opts.pollIntervalMs != null) {
        const n = Number(opts.pollIntervalMs);
        if (Number.isFinite(n) && n >= 15_000) pollIntervalMs = Math.round(n);
    }
    if (opts.pollSource === 'rtdb' || opts.pollSource === 'api') {
        pollSource = opts.pollSource;
    }
    rebuildTransport();
}

export function getNjordLiveTransport() {
    return {
        mode: transportMode,
        pollIntervalMs,
        pollSource,
        visibilityPaused,
        realtimeFocusCount: realtimeFocusIds.size,
    };
}

/**
 * Explicit sensor list (e.g. after registry load). Merges with existing hub ids.
 * @param {string[]} ids
 */
export function syncNjordLiveSensorIds(ids) {
    if (!Array.isArray(ids) || !ids.length) return;
    const merged = [...new Set([...sensorIds, ...ids.map((id) => String(id).trim()).filter(Boolean)])];
    applySensorIds(merged);
}

/**
 * @param {(data: Record<string, object>) => void} cb
 * @returns {() => void} unsubscribe
 */
export function subscribeNjordLiveHub(cb) {
    if (typeof cb !== 'function') return () => {};
    bindVisibility();
    listeners.add(cb);
    ensureRegistrySync();
    cb(buildSnapshot());
    if (!sensorIds.length) refreshSensorIdsFromRegistry();
    rebuildTransport();
    return () => {
        listeners.delete(cb);
        if (!listeners.size) {
            teardownAllRealtimeListeners();
            clearPollTimer();
        }
    };
}

/**
 * One-sensor shallow live (sensor detail). Uses realtime for this id only while
 * the facility hub can stay on poll/api.
 * @param {string} sensorId
 * @param {(row: object|null) => void} cb
 * @returns {() => void} unsubscribe
 */
export function subscribeNjordLiveSensorShallow(sensorId, cb) {
    const id = String(sensorId || '').trim();
    if (!id) {
        cb(null);
        return () => {};
    }
    bindVisibility();
    realtimeFocusIds.add(id);
    if (!sensorIds.includes(id)) {
        applySensorIds([...sensorIds, id]);
    }
    ensureSensorListeners(id);
    const wrapped = (all) => cb(all[id] || null);
    listeners.add(wrapped);
    cb(rowsBySensor.get(id) || null);
    rebuildTransport();
    return () => {
        listeners.delete(wrapped);
        realtimeFocusIds.delete(id);
        if (!wantsRealtimeListeners(id)) {
            teardownSensorListeners(id);
        }
        if (!listeners.size) {
            teardownAllRealtimeListeners();
            clearPollTimer();
        } else {
            rebuildTransport();
        }
    };
}

/** Test-only reset */
export function __resetNjordLiveHubForTests() {
    [...unsubsBySensor.keys()].forEach((id) => {
        teardownSensorListeners(id);
        rowsBySensor.delete(id);
    });
    listeners.clear();
    realtimeFocusIds.clear();
    sensorIds = [];
    transportMode = 'poll';
    pollIntervalMs = NJORD_LIVE_POLL_INTERVAL_MS;
    pollSource = 'api';
    visibilityPaused = false;
    clearPollTimer();
    if (registrySyncTimer != null) {
        clearInterval(registrySyncTimer);
        registrySyncTimer = null;
    }
    registrySyncInFlight = false;
    if (notifyRaf != null) {
        if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(notifyRaf);
        else clearTimeout(notifyRaf);
        notifyRaf = null;
    }
}
